/**
 * NotificationsMenu — the app bar bell, its unread badge, and the dropdown.
 *
 * Port of the legacy Headerbar notification menu (Headerbar.vue:75-118).
 *
 * Not the library `Menu`: its MenuOption is a label plus an icon, and these rows
 * carry a title, a message, a timestamp and a read/unread state. So this is a
 * small popover of its own, with the same dismissal behaviour as Dropdown
 * (outside pointer-down, Escape).
 *
 * Faithful to legacy: 15s polling, rows in API order (legacy never sorts), the
 * server's `Count` as the badge, click = mark read then open the notification's
 * portfolio on Manage Portfolio, and Clear All deleting everything with no
 * confirmation.
 *
 * Fixed rather than reproduced: legacy keys rows on `notification.notificationId`,
 * a field the payload does not have, so every key is `undefined`. Keyed on `_id`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { NOTIFICATIONS_EMPTY } from '@config/headerActions';
import { BellIcon } from '@components/common/icons';
import { NotificationBadge, Tooltip } from '@components/ui';
import { selectTelemetryJobStatus, setTelemetryJobStatus } from '@features/dashboard';
import { useNotify } from '@hooks/useNotify';
import { cx } from '@lib/classNames';
import { selectUser } from '@security/auth/authSlice';
import { baseApi } from '@services/baseApi';

import {
  useClearAllNotificationsMutation,
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
} from '../../api/notificationsApi';
import { formatNotificationDate } from '../../formatNotificationDate';
import { finishedTelemetryJobs } from '../../telemetryJobs';

import styles from './NotificationsMenu.module.css';

/** Legacy's cadence, Headerbar.vue:328. */
const POLL_INTERVAL_MS = 15000;

export function NotificationsMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  /*
   * The SIGNED-IN user, not a constant.
   *
   * This asked for a hard-coded address while the login flow was still a demo.
   * Once real sign-in landed that became a 403: the server checks the token's
   * subject against user_email, so requesting anyone else's notifications is
   * correctly refused. Legacy reads the same value from its `email` cookie
   * (apiservice.js:903).
   *
   * `skip` rather than a fallback address: with no session there is no one to
   * fetch for, and guessing produces exactly the mismatch described above.
   */
  const user = useSelector(selectUser);
  const userEmail = user?.email ?? '';

  // RTK Query owns the timer: it starts with the first subscriber and stops on
  // unmount. Legacy's clearInterval sat in a Vue 2 hook that never fired, so
  // its poll ran forever — that class of bug cannot happen here.
  const { data } = useGetNotificationsQuery(userEmail, {
    pollingInterval: POLL_INTERVAL_MS,
    skip: !userEmail,
  });
  const [markRead] = useMarkNotificationReadMutation();
  const [clearAll] = useClearAllNotificationsMutation();

  const items = data?.items ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  /*
   * A telemetry portfolio finished: its "telemetry" notification arrives, and
   * the Pending / In Progress mark on its sidebar row goes (legacy Headerbar
   * pollNotifications :654-680). The list is fetched again too, so the row
   * shows the portfolio as it now is (legacy refreshTelemetryJobStatus). Runs
   * on every page — the bell is always there, as legacy's poll was.
   */
  const dispatch = useDispatch();
  const notify = useNotify();
  const jobs = useSelector(selectTelemetryJobStatus);
  useEffect(() => {
    const done = finishedTelemetryJobs(data?.items, jobs);
    if (done.length === 0) return;
    for (const id of done) dispatch(setTelemetryJobStatus({ id, status: 'completed' }));
    dispatch(baseApi.util.invalidateTags(['Portfolio']));
  }, [data, jobs, dispatch]);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) close();
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  /**
   * Mark read (in the background) and go to Manage Portfolio and OPEN the notification's
   * portfolio (legacy Headerbar.vue markAsRead / navigateFromNotification).
   *
   * Only the id travels, in the navigation state: the sidebar list on that page
   * finds the portfolio and opens it through the same path as a click on its
   * row — so the unsaved-changes prompt and the large-data rules apply, which
   * legacy's direct store write skipped. A failed mark-read still navigates
   * (RTK resolves with { error }, it does not throw), as legacy.
   */
  const handleSelect = (notification) => {
    close();
    // Not awaited (user order 2026-10-19): the page change no longer waits for
    // the mark-read round trip. The badge updates when the call lands.
    if (!notification.is_seen) markRead(notification._id);
    // Its portfolio is still being read: nothing to open yet. Legacy silently
    // did nothing here (Headerbar.vue :477-479); say why instead.
    if (notification.portfolio_id && jobs[notification.portfolio_id]) {
      notify.info('This portfolio is still being prepared. Open it once it is ready.');
      return;
    }
    navigate('/manage-portfolio', {
      state: notification.portfolio_id ? { openPortfolioId: notification.portfolio_id } : undefined,
    });
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <Tooltip label="Notifications" side="bottom">
        <button
          type="button"
          id="btn-header-notifications"
          className={styles.bell}
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {unreadCount > 0 ? (
            <NotificationBadge count={unreadCount}>
              <BellIcon />
            </NotificationBadge>
          ) : (
            <BellIcon />
          )}
        </button>
      </Tooltip>

      {open ? (
        <div className={styles.panel} role="menu" aria-label="Notifications">
          {items.length ? (
            <>
              <ul className={styles.list}>
                {items.map((n) => (
                  <li key={n._id}>
                    <button
                      type="button"
                      role="menuitem"
                      className={cx(styles.item, n.is_seen ? styles.read : styles.unread)}
                      onClick={() => handleSelect(n)}
                    >
                      <span className={styles.title}>{n.title}</span>
                      <span className={styles.message}>{n.message}</span>
                      <span className={styles.date}>{formatNotificationDate(n.created_at)}</span>
                    </button>
                  </li>
                ))}
              </ul>

              <div className={styles.footer}>
                <button
                  type="button"
                  className={styles.clearAll}
                  onClick={() => clearAll(userEmail)}
                >
                  Clear All
                </button>
              </div>
            </>
          ) : (
            <p className={styles.empty}>{NOTIFICATIONS_EMPTY}</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default NotificationsMenu;
